/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Plus } from 'lucide-react';
import { useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { QueryState } from '../../components/ui/query-state.js';
import {
  type WaitlistEntry,
  type WaitlistPriority,
  type WaitlistStatus,
} from '../../lib/api/waitlist.js';
import { useUpdateWaitlistEntry, useWaitlist } from '../../lib/queries/waitlist.js';
import { formatIst } from '../../lib/utils/datetime.js';

import { AddWaitlistModal } from './waitlist-modal.js';

const PRIORITY_TONE: Readonly<Record<WaitlistPriority, BadgeTone>> = {
  high: 'rose',
  medium: 'amber',
  low: 'slate',
};
const STATUS_TONE: Readonly<Record<WaitlistStatus, BadgeTone>> = {
  waiting: 'violet',
  contacted: 'amber',
  scheduled: 'green',
  removed: 'slate',
};
const COLS = ['child', 'age', 'concern', 'slots', 'priority', 'waitSince', 'status'] as const;

function WaitlistRow({ item }: { readonly item: WaitlistEntry }): JSX.Element {
  const { t } = useTranslation();
  const update = useUpdateWaitlistEntry();
  const contact = useCallback(() => {
    update.mutate({ id: item.id, payload: { status: 'contacted' } });
  }, [update, item.id]);
  const schedule = useCallback(() => {
    update.mutate({ id: item.id, payload: { status: 'scheduled' } });
  }, [update, item.id]);
  const remove = useCallback(() => {
    update.mutate({ id: item.id, payload: { status: 'removed' } });
  }, [update, item.id]);
  return (
    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/60">
      <td className="px-5 py-3 font-medium text-slate-800 dark:text-white">{item.childName}</td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{item.age ?? '—'}</td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{item.concern ?? '—'}</td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{item.preferredSlots ?? '—'}</td>
      <td className="px-5 py-3">
        <Badge
          label={t(`waitlist.priorities.${item.priority}`)}
          tone={PRIORITY_TONE[item.priority]}
        />
      </td>
      <td className="px-5 py-3 text-slate-600 dark:text-slate-300">
        {formatIst(`${item.waitSince}T00:00:00.000Z`, 'dd MMM yyyy')}
      </td>
      <td className="px-5 py-3">
        <Badge label={t(`waitlist.statuses.${item.status}`)} tone={STATUS_TONE[item.status]} />
      </td>
      <td className="px-5 py-3 text-right">
        <div className="flex justify-end gap-1">
          {item.status === 'waiting' && (
            <Button variant="ghost" size="sm" onClick={contact} disabled={update.isPending}>
              {t('waitlist.contact')}
            </Button>
          )}
          {item.status !== 'scheduled' && item.status !== 'removed' && (
            <Button variant="ghost" size="sm" onClick={schedule} disabled={update.isPending}>
              {t('waitlist.schedule')}
            </Button>
          )}
          {item.status !== 'removed' && (
            <Button variant="ghost" size="sm" onClick={remove} disabled={update.isPending}>
              {t('waitlist.remove')}
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

/** Intake waitlist (BRD M08; Figma "Waitlist"). */
export function WaitlistPage(): JSX.Element {
  const { t } = useTranslation();
  const [addOpen, setAddOpen] = useState(false);
  const query = useWaitlist();
  const rows = (query.data ?? []).filter((r) => r.status !== 'removed');
  const openAdd = useCallback(() => {
    setAddOpen(true);
  }, []);
  const closeAdd = useCallback(() => {
    setAddOpen(false);
  }, []);

  return (
    <div className="p-8">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <PageHeader title={t('waitlist.title')} subtitle={t('waitlist.subtitle')} />
        <Button type="button" onClick={openAdd}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('waitlist.addTitle')}
        </Button>
      </div>

      <QueryState
        isLoading={query.isLoading}
        isError={query.isError}
        isEmpty={rows.length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('waitlist.empty')}
      >
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
              <tr>
                {COLS.map((c) => (
                  <th key={c} className="px-5 py-3 font-semibold">
                    {t(`waitlist.col.${c}`)}
                  </th>
                ))}
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {rows.map((item) => (
                <WaitlistRow key={item.id} item={item} />
              ))}
            </tbody>
          </table>
        </div>
      </QueryState>

      <AddWaitlistModal open={addOpen} onClose={closeAdd} />
    </div>
  );
}
